import {
  Component,
  input,
  output,
  inject,
  ViewChild,
  ElementRef,
  AfterViewInit,
  OnDestroy,
  signal,
  effect,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { SharedModule } from '../../shared.module';
import { ProgressbarComponent } from '../progressbar/progressbar.component';
import { CatalogCardImpactComponent } from '../catalog-card-impact/catalog-card-impact.component';
import { FoundationBadge } from '@gs-2.0/core';

export interface CatalogCardData {
  uuid: string;
  languageCode: string;
  imageUrl: string;
  title: string;
  location: string;
  organizationName?: string;
  categories: string[];
  countries?: string[];
  regions?: string[];
  globalInfluence?: boolean;
  cause: string;
  description: string;
  currentAmount: number;
  totalAmount: number;
  daysLeft: number;
  currency?: string;
  foundationBadges?: FoundationBadge[];
  giveLink?: string;
}

@Component({
  selector: 'app-catalog-card',
  standalone: true,
  imports: [CommonModule, SharedModule, ProgressbarComponent, CatalogCardImpactComponent],
  templateUrl: './catalog-card.component.html',
  styleUrls: ['./catalog-card.component.scss'],
})
export class CatalogCardComponent implements AfterViewInit, OnDestroy {
  private router = inject(Router);

  @ViewChild('categoriesContainer', { read: ElementRef })
  categoriesContainer?: ElementRef<HTMLElement>;

  @ViewChild('cardContent', { read: ElementRef })
  cardContent?: ElementRef<HTMLElement>;

  @ViewChild('imageContainer', { read: ElementRef })
  imageContainer?: ElementRef<HTMLElement>;

  data = input.required<CatalogCardData>();
  showProgressBar = input<boolean>(true);
  variant = input<'list' | 'tile'>('list');
  entityType = input<'charity' | 'project'>('charity');
  emitEvents = input<boolean>(false);

  // Event outputs
  giveClicked = output<CatalogCardData>();
  learnMoreClicked = output<CatalogCardData>();

  // Admin mode: shows Preview / Edit / Delete actions instead of Give / Learn More
  adminMode = input<boolean>(false);
  showDelete = input<boolean>(true);

  // Opt-in read-only status pill overlaid on the card image.
  // Renders only when statusLabel is provided, so other consumers are unaffected.
  statusLabel = input<string>('');
  statusClass = input<string>('');
  previewClicked = output<CatalogCardData>();
  editClicked = output<CatalogCardData>();
  deleteClicked = output<CatalogCardData>();

  maxVisibleCategories = signal(3);
  categoriesExpanded = false;
  private resizeObserver?: ResizeObserver;
  private contentResizeObserver?: ResizeObserver;

  constructor() {
    // Watch for data changes and recalculate
    effect(() => {
      const data = this.data();
      if (data.categories) {
        // Recalculate the category badge fit when the card's data changes.
        setTimeout(() => {
          this.calculateVisibleCategories();
        }, 0);
      }
    });
  }

  ngAfterViewInit(): void {
    this.calculateVisibleCategories();

    // Initial height sync after view is initialized
    setTimeout(() => {
      this.syncImageContainerHeight();
    }, 0);

    // Set up ResizeObserver to recalculate on container resize
    if (this.categoriesContainer) {
      this.resizeObserver = new ResizeObserver(() => {
        this.calculateVisibleCategories();
      });
      this.resizeObserver.observe(this.categoriesContainer.nativeElement);
    }

    // Set up ResizeObserver to sync image container height with content height
    if (this.cardContent) {
      this.contentResizeObserver = new ResizeObserver(() => {
        this.syncImageContainerHeight();
      });
      this.contentResizeObserver.observe(this.cardContent.nativeElement);
    }
  }

  ngOnDestroy(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.contentResizeObserver) {
      this.contentResizeObserver.disconnect();
    }
  }

  /**
   * The Impact Area / Focus Areas box replaces the category badges on donor-facing
   * charity cards only. Admin charity cards keep their existing category badges.
   */
  get showImpactBox(): boolean {
    return this.entityType() === 'charity' && !this.adminMode();
  }

  private syncImageContainerHeight(): void {
    if (!this.cardContent || !this.imageContainer) {
      return;
    }

    // On mobile (max-width: 1000px), keep image height fixed at 163px
    const isMobile = window.innerWidth <= 1000;
    if (isMobile) {
      this.imageContainer!.nativeElement.style.height = '163px';
      return;
    }

    // For tile variant on desktop, keep image height fixed at 262px
    if (this.variant() === 'tile') {
      this.imageContainer!.nativeElement.style.height = '262px';
      return;
    }

    // Desktop list variant: match the content height, regardless of expanded state
    // Use requestAnimationFrame to ensure layout is complete
    requestAnimationFrame(() => {
      const contentHeight = this.cardContent!.nativeElement.offsetHeight;
      const minHeight = 294; // Minimum height when collapsed

      // Use the content height, but ensure it's at least the minimum
      const targetHeight = Math.max(contentHeight, minHeight);

      this.imageContainer!.nativeElement.style.height = `${targetHeight}px`;
    });
  }

  private calculateVisibleCategories(): void {
    if (!this.categoriesContainer || this.categoriesExpanded) {
      return;
    }

    const container = this.categoriesContainer.nativeElement;
    const categories = this.data().categories;

    if (!categories || categories.length === 0) {
      return;
    }

    // Start with all categories and reduce until they fit in one line
    let testCount = Math.min(categories.length, 5); // Max 5 to start
    let fitsInOneLine = false;

    // Create a temporary container to test rendering
    const tempContainer = document.createElement('div');
    tempContainer.style.cssText = window.getComputedStyle(container).cssText;
    tempContainer.style.position = 'absolute';
    tempContainer.style.visibility = 'hidden';
    tempContainer.style.width = `${container.offsetWidth}px`;
    tempContainer.style.display = 'flex';
    tempContainer.style.gap = '8px';
    tempContainer.style.flexWrap = 'nowrap';
    tempContainer.style.alignItems = 'center';
    document.body.appendChild(tempContainer);

    while (testCount >= 1 && !fitsInOneLine) {
      tempContainer.innerHTML = '';
      let totalWidth = 0;

      // Add badges for testing — match actual badge styles to get accurate measurements
      for (let i = 0; i < testCount; i++) {
        const badge = document.createElement('span');
        badge.textContent = categories[i];
        badge.style.display = 'inline-flex';
        badge.style.padding = '4px 12px';
        badge.style.whiteSpace = 'nowrap';
        badge.style.fontSize = '12px';
        badge.style.fontFamily = 'Rethink Sans, sans-serif';
        badge.style.boxSizing = 'border-box';
        tempContainer.appendChild(badge);
      }

      // Add overflow badge if needed
      if (testCount < categories.length) {
        const overflowBadge = document.createElement('span');
        overflowBadge.textContent = `+${categories.length - testCount} more`;
        overflowBadge.style.display = 'inline-flex';
        overflowBadge.style.padding = '2px 10px';
        overflowBadge.style.whiteSpace = 'nowrap';
        overflowBadge.style.fontSize = '10px';
        overflowBadge.style.fontFamily = 'Rethink Sans, sans-serif';
        overflowBadge.style.boxSizing = 'border-box';
        tempContainer.appendChild(overflowBadge);
      }

      // Calculate total width including gaps
      const children = Array.from(tempContainer.children) as HTMLElement[];
      totalWidth = children.reduce(
        (sum, child) => sum + child.offsetWidth,
        0
      );
      totalWidth += (children.length - 1) * 8; // Add gap width

      // Check if it fits
      if (totalWidth <= container.offsetWidth) {
        fitsInOneLine = true;
        this.maxVisibleCategories.set(testCount);
      } else {
        testCount--;
      }
    }

    document.body.removeChild(tempContainer);

    // Ensure at least 1 category is visible
    if (!fitsInOneLine && testCount < 1) {
      this.maxVisibleCategories.set(1);
    }
  }

  get visibleCategories(): string[] {
    if (this.categoriesExpanded) {
      return this.data().categories;
    }
    return this.data().categories.slice(0, this.maxVisibleCategories());
  }

  get hasOverflowCategories(): boolean {
    return this.data().categories.length > this.maxVisibleCategories();
  }

  get overflowCount(): number {
    return this.data().categories.length - this.maxVisibleCategories();
  }

  onGiveClick(): void {
    if (this.emitEvents()) {
      // Emit event if parent component is listening
      this.giveClicked.emit(this.data());
    } else {
      // Default behavior (placeholder for future implementation)
      console.log('Give clicked for:', this.data().title);
    }
  }

  get cardUrl(): string {
    const { uuid, languageCode } = this.data();
    const type = this.entityType();
    return `/${type}/${uuid}/${languageCode}/summary`;
  }

  /**
   * Navigate to the entity details/summary page. Used by every "+N more" control
   * on the card, which redirects instead of expanding the list in place.
   */
  goToDetails(event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (this.emitEvents()) {
      this.learnMoreClicked.emit(this.data());
    } else {
      const { uuid, languageCode } = this.data();
      this.router.navigate([this.entityType(), uuid, languageCode, 'summary']);
    }
  }

  onPreviewClick(): void {
    this.previewClicked.emit(this.data());
  }

  onEditClick(): void {
    this.editClicked.emit(this.data());
  }

  onDeleteClick(): void {
    this.deleteClicked.emit(this.data());
  }

  onLearnMoreClick(event?: MouseEvent): void {
    if (this.adminMode()) return;

    // For desktop links, prevent default if it's not a ctrl/cmd/middle click
    if (event && !event.ctrlKey && !event.metaKey && event.button !== 1) {
      event.preventDefault();
    }

    if (this.emitEvents()) {
      // Emit event if parent component is listening (only for regular clicks)
      if (!event || (!event.ctrlKey && !event.metaKey && event.button !== 1)) {
        this.learnMoreClicked.emit(this.data());
      }
    } else {
      // Default behavior: navigate programmatically if it's a regular click (not opening in new tab)
      if (!event || (!event.ctrlKey && !event.metaKey && event.button !== 1)) {
        const { uuid, languageCode } = this.data();
        const type = this.entityType();
        this.router.navigate([type, uuid, languageCode, 'summary']);
      }
    }
  }
}
